// frontend/src/components/chat/MessageBubble.jsx
import React from 'react'
import { motion } from 'framer-motion'
import { Bot, User, Image, ExternalLink, Clock } from 'lucide-react'

const MessageBubble = ({ message }) => {
  const isUser = message.role === 'user'
  const isSystem = message.role === 'system'

  const getMessageIcon = () => {
    if (isUser) return <User className="h-4 w-4" />
    if (isSystem) return <Clock className="h-4 w-4" />
    return <Bot className="h-4 w-4" />
  }

  const getMessageStyles = () => {
    if (isUser) {
      return {
        container: 'flex justify-end',
        bubble: 'bg-primary-600 text-white',
        arrow: 'border-l-primary-600'
      }
    }
    if (isSystem) {
      return {
        container: 'flex justify-center',
        bubble: 'bg-gray-100 text-gray-700 text-sm',
        arrow: ''
      }
    }
    return {
      container: 'flex justify-start',
      bubble: 'bg-gray-100 text-gray-900',
      arrow: 'border-r-gray-100'
    }
  }

  const styles = getMessageStyles()

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return ''
    const date = new Date(timestamp)
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  const renderMessageContent = () => {
    if (message.type === 'image' && message.imageUrl) {
      return (
        <div className="space-y-2">
          <img 
            src={message.imageUrl} 
            alt="Screenshot"
            className="max-w-sm rounded-lg border border-gray-200"
          />
          {message.content && <p>{message.content}</p>}
        </div>
      )
    }

    if (message.type === 'action') {
      return (
        <div className="space-y-2">
          <div className="flex items-center space-x-2 text-sm font-medium">
            <ExternalLink className="h-4 w-4" />
            <span>Action: {message.action}</span>
          </div>
          {message.content && <p className="text-sm">{message.content}</p>}
          {message.url && (
            <a 
              href={message.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-primary-600 hover:text-primary-700 underline"
            >
              {message.url}
            </a>
          )}
        </div>
      )
    }

    return <p className="whitespace-pre-wrap">{message.content}</p>
  }

  if (isSystem) {
    return (
      <div className={styles.container}>
        {/* <div className={`inline-flex items-center space-x-2 px-3 py-1 rounded-full ${styles.bubble}`}>
          {getMessageIcon()}
          <span>{message.content}</span>
        </div> */}
      </div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={styles.container}
    >
      <div className="max-w-xs lg:max-w-md">
        <div className={`relative px-4 py-3 rounded-2xl ${styles.bubble}`}>
          {/* Message content */}
          <div className="text-sm">
            {renderMessageContent()}
          </div>

          {/* Timestamp */}
          {message.timestamp && (
            <div className={`text-xs mt-2 opacity-70 ${isUser ? 'text-primary-100' : 'text-gray-500'}`}>
              {formatTimestamp(message.timestamp)}
            </div>
          )}
        </div>

        {/* Avatar and metadata */}
        <div className={`flex items-center space-x-2 mt-1 ${isUser ? 'justify-end' : 'justify-start'}`}>
          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
            isUser ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-600'
          }`}>
            {getMessageIcon()}
          </div>
          <span className="text-xs text-gray-500">
            {isUser ? 'You' : 'AI Assistant'}
          </span>
          {message.status && (
            <span className={`text-xs px-2 py-1 rounded-full ${
              message.status === 'delivered' ? 'bg-success-100 text-success-700' :
              message.status === 'pending' ? 'bg-warning-100 text-warning-700' :
              'bg-gray-100 text-gray-600'
            }`}>
              {message.status}
            </span>
          )}
        </div>
      </div>
    </motion.div>
  )
}

export default MessageBubble